import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Package, Truck, Search, Clock, CheckCircle2, Plus, Trash2, ArrowRight, Warehouse, FileText, AlertCircle, Eye, Pencil, Ship } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NATURE_OPTIONS = [
  { value: "Purchase-RMT/FNG", label: "Purchase - RMT/FNG" },
  { value: "Purchase-CTM", label: "Purchase - CTM" },
  { value: "Import Purchase", label: "Import Purchase" },
  { value: "Jobwork-Own", label: "Jobwork - Own" },
  { value: "Jobwork-Client", label: "Jobwork - Client" },
  { value: "Sales Return", label: "Sales Return" },
  { value: "Samples Returned", label: "Samples Returned" },
  { value: "Samples Received", label: "Samples Received" }
];

const QUALITY_OPTIONS = [
  { value: "Accepted", label: "Accepted" },
  { value: "Rejected", label: "Rejected" },
  { value: "Partial", label: "Partial Accept/Reject" }
];

const RETURN_STATUS_OPTIONS = [
  { value: "immediate", label: "Return Immediately" },
  { value: "later", label: "Return Later" }
];

const routingColors = {
  purchase: 'bg-blue-100 text-blue-800',
  ojc: 'bg-purple-100 text-purple-800',
  jobwork_client: 'bg-orange-100 text-orange-800',
  sales: 'bg-pink-100 text-pink-800',
  samples_issued: 'bg-cyan-100 text-cyan-800',
  samples_received: 'bg-teal-100 text-teal-800'
};

export default function StoreReceivingPage() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gateEntries, setGateEntries] = useState([]);
  const [storeReceipts, setStoreReceipts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Dialog states
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [selectedGateEntry, setSelectedGateEntry] = useState(null);
  const [lookupDialogOpen, setLookupDialogOpen] = useState(false);
  const [lookupGateNo, setLookupGateNo] = useState('');
  
  // View/Edit dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  
  // PO cache per item (changed from indentsByItem)
  const [posByItem, setPosByItem] = useState({});
  
  // Import shipments for Import Purchase nature
  const [importShipments, setImportShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(null);
  
  // Pending materials for store guidance
  const [pendingMaterials, setPendingMaterials] = useState([]);
  const [pendingMaterialsLoading, setPendingMaterialsLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [pendingMaterialsLocationFilter, setPendingMaterialsLocationFilter] = useState('all');
  // OJC inward redesign Phase 1 (Apr 2026) — open OJCs for the selected supplier
  const [openOjcsForSupplier, setOpenOjcsForSupplier] = useState([]);
  // True when the fetched OJC set for this gate entry contained only
  // grandfathered (pre-Phase 1) OJCs that are filtered out of the dropdown —
  // drives the amber empty-state banner and the "no eligible OJCs" hint.
  const [hasGrandfatheredOnly, setHasGrandfatheredOnly] = useState(false);
  
  // Form state
  const [receiptForm, setReceiptForm] = useState({
    supplier_id: '',
    supplier_name: '',
    invoice_dc_no: '',
    nature_of_inward: '',
    sample_returnable: null,
    debit_note_required: false,  // For rejected goods being returned later
    import_shipment_id: '',  // For Import Purchase
    import_shipment_number: '',
    items: []
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [gateRes, receiptsRes, suppliersRes, itemsRes, subLocRes, locRes] = await Promise.all([
        axios.get(`${API_URL}/store/gate-entries?status=pending`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/store/receipts`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/items`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/sub-locations`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/locations`, { headers: getAuthHeader() })
      ]);
      
      setGateEntries(gateRes.data);
      setStoreReceipts(receiptsRes.data);
      setSuppliers(suppliersRes.data);
      setItems(itemsRes.data);
      setSubLocations(subLocRes.data.filter(sl => sl.is_active !== false));
      setLocations(locRes.data.filter(l => l.is_active !== false));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingMaterials = async () => {
    setPendingMaterialsLoading(true);
    try {
      const params = pendingMaterialsLocationFilter !== 'all' ? `?location_id=${pendingMaterialsLocationFilter}` : '';
      const response = await axios.get(`${API_URL}/store/pending-materials${params}`, { headers: getAuthHeader() });
      setPendingMaterials(response.data);
    } catch (error) {
      console.error('Error fetching pending materials:', error);
      toast.error('Failed to load pending materials');
    } finally {
      setPendingMaterialsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch pending materials when tab changes or filter changes
  useEffect(() => {
    if (activeTab === 'pending-materials') {
      fetchPendingMaterials();
    }
  }, [activeTab, pendingMaterialsLocationFilter]);

  // Fetch POs for a specific item (changed from fetchIndentsForItem)
  const fetchPOsForItem = async (itemId) => {
    if (!itemId || posByItem[itemId]) return;
    
    try {
      const response = await axios.get(`${API_URL}/purchase-orders/by-item/${itemId}`, { headers: getAuthHeader() });
      setPosByItem(prev => ({ ...prev, [itemId]: response.data }));
    } catch (error) {
      console.error('Error fetching POs for item:', error);
    }
  };

  // Fetch pending import shipments for a supplier
  const fetchImportShipments = async (supplierId) => {
    if (!supplierId) {
      setImportShipments([]);
      return;
    }
    
    try {
      // Fetch shipments with status duty_paid or boe_received (ready for goods receipt)
      const response = await axios.get(
        `${API_URL}/import-shipments?supplier_id=${supplierId}&status=duty_paid`,
        { headers: getAuthHeader() }
      );
      setImportShipments(response.data.shipments || []);
    } catch (error) {
      console.error('Error fetching import shipments:', error);
      setImportShipments([]);
    }
  };

  // Handle import shipment selection - populate items from shipment
  const handleShipmentSelect = async (shipmentId) => {
    if (!shipmentId) {
      setSelectedShipment(null);
      setReceiptForm(prev => ({
        ...prev,
        import_shipment_id: '',
        import_shipment_number: '',
        items: [createEmptyItem()]
      }));
      return;
    }

    const shipment = importShipments.find(s => s.id === shipmentId);
    if (!shipment) return;

    setSelectedShipment(shipment);
    
    // Populate items from shipment with landed cost info
    const populatedItems = (shipment.line_items || []).map(li => ({
      item_id: li.item_id,
      item_name: li.item_name,
      item_unit: li.unit || 'NOS',
      quantity_received: li.quantity?.toString() || '0',  // Pre-fill with expected qty, editable
      quantity_rejected: '0',
      quantity_accepted: li.quantity?.toString() || '0',
      return_status: '',
      quality_notes: '',
      po_id: shipment.po_id || '',
      po_number: shipment.po_number || '',
      po_line_item_index: null,
      sub_location: '',
      system_unit: li.unit || 'NOS',
      // Import-specific fields
      landed_cost_per_unit: li.landed_cost_per_unit || 0,
      supplier_price_inr: li.supplier_price_inr || 0,
      duty_per_unit: li.duty_per_unit || 0,
      freight_per_unit: li.freight_per_unit || 0,
      other_costs_per_unit: li.other_costs_per_unit || 0
    }));

    setReceiptForm(prev => ({
      ...prev,
      import_shipment_id: shipmentId,
      import_shipment_number: shipment.shipment_number,
      supplier_id: shipment.supplier_id,
      supplier_name: shipment.supplier_name,
      invoice_dc_no: shipment.boe_number || shipment.shipment_number,
      items: populatedItems.length > 0 ? populatedItems : [createEmptyItem()]
    }));
  };

  const handleLookup = async () => {
    if (!lookupGateNo.trim()) {
      toast.error('Please enter a gate entry number');
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/store/gate-entry/${lookupGateNo.trim()}`);
      if (response.data) {
        openProcessDialog(response.data);
        setLookupDialogOpen(false);
        setLookupGateNo('');
      }
    } catch (error) {
      toast.error('Gate entry not found');
    }
  };

  const openProcessDialog = async (entry) => {
    setSelectedGateEntry(entry);
    setPosByItem({}); // Reset PO cache
    setSelectedShipment(null); // Reset shipment
    setImportShipments([]); // Reset shipments list
    setOpenOjcsForSupplier([]);
    setHasGrandfatheredOnly(false);
    setReceiptForm({
      supplier_id: '',
      supplier_name: entry.supplier_name_raw || '',
      invoice_dc_no: entry.invoice_dc_no || '',
      nature_of_inward: entry.nature_of_inward || '',
      sample_returnable: null,
      import_shipment_id: '',
      import_shipment_number: '',
      items: [createEmptyItem()]
    });
    // OJC inward redesign Phase 1 (Apr 2026) — when opening a Jobwork-Own gate
    // entry, fetch the open (non-grandfathered, not-completed) OJCs so each
    // line can be linked. Any supplier can be a jobber, so no supplier-match
    // filter is applied — the full open-OJC list is shown.
    if ((entry.nature_of_inward || '') === 'Jobwork-Own') {
      try {
        const resp = await axios.get(`${API_URL}/ojc/register`, { headers: getAuthHeader() });
        const allOjcs = resp.data || [];
        const openOjcs = allOjcs.filter(o =>
          o.status !== 'completed' &&
          o.status !== 'cancelled'
        );
        const eligible = openOjcs.filter(o => !o.grandfathered);
        setOpenOjcsForSupplier(eligible);
        // If there are open OJCs but none are eligible (all grandfathered),
        // flag the empty-state so the UI can display the Phase 1 explanation.
        setHasGrandfatheredOnly(openOjcs.length > 0 && eligible.length === 0);
      } catch (err) {
        console.error('Failed to fetch open OJCs:', err);
        setHasGrandfatheredOnly(false);
      }
    }
    setProcessDialogOpen(true);
  };

  const createEmptyItem = () => ({
    item_id: '',
    item_name: '',
    item_unit: '',
    quantity_received: '',
    quantity_rejected: '0',
    quantity_accepted: '',  // Auto-calculated: received - rejected
    return_status: '',  // 'immediate' or 'later' - only when quantity_rejected > 0
    quality_notes: '',
    po_id: '',
    po_number: '',
    po_line_item_index: null,
    sub_location: '',  // Optional - exact storage location
    system_unit: '',    // System UOM from PO (only system UOM used in Store Receiving)
    // Import-specific fields (populated from shipment)
    landed_cost_per_unit: 0,
    supplier_price_inr: 0,
    duty_per_unit: 0,
    freight_per_unit: 0,
    other_costs_per_unit: 0,
    // Phase 1 (Apr 2026) — OJC linkage for Jobwork-Own receipts
    ojc_register_id: ''
  });

  const addItemRow = () => {
    setReceiptForm({
      ...receiptForm,
      items: [...receiptForm.items, createEmptyItem()]
    });
  };

  const removeItemRow = (index) => {
    if (receiptForm.items.length > 1) {
      const newItems = receiptForm.items.filter((_, i) => i !== index);
      setReceiptForm({ ...receiptForm, items: newItems });
    }
  };

  const updateItem = async (index, field, value) => {
    const newItems = [...receiptForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Auto-fill item name and unit when item is selected
    if (field === 'item_id' && value) {
      const selectedItem = items.find(i => i.id === value);
      if (selectedItem) {
        newItems[index].item_name = selectedItem.name;
        newItems[index].item_unit = selectedItem.unit || 'PCS';
        // Pre-fill sub_location from item master (use sub_location code or legacy sub_location field)
        const itemSubLoc = selectedItem.sub_location || '';
        // Check if this code exists in sub_locations master
        const matchingSubLoc = subLocations.find(sl => sl.code === itemSubLoc || sl.id === selectedItem.sub_location_id);
        newItems[index].sub_location = matchingSubLoc ? matchingSubLoc.code : '';
        // Reset PO when item changes
        newItems[index].po_id = '';
        newItems[index].po_number = '';
        newItems[index].po_line_item_index = null;
        // Reset quantities
        newItems[index].quantity_received = '';
        newItems[index].quantity_rejected = '0';
        newItems[index].quantity_accepted = '';
        newItems[index].return_status = '';
        // Fetch POs for this item
        await fetchPOsForItem(value);
      }
    }
    
    // Auto-fill PO number when PO is selected
    if (field === 'po_id' && value) {
      // Bug 9 fix: dropdown emits composite '<po_id>::<line_item_index>' so
      // duplicate-item rows on the same PO stay distinguishable. Split here
      // and store BOTH keys so the backend receive-handler lands the qty on
      // the correct line (store.py:4858-4866 reads po_line_item_index).
      const [rawPoId, rawIdx] = String(value).split('::');
      const parsedIdx = rawIdx !== undefined && rawIdx !== '' ? parseInt(rawIdx, 10) : null;
      newItems[index].po_id = rawPoId;
      newItems[index].po_line_item_index = Number.isNaN(parsedIdx) ? null : parsedIdx;

      const itemPOs = posByItem[newItems[index].item_id] || [];
      const selectedPO = itemPOs.find(p => p.po_id === rawPoId && p.line_item_index === parsedIdx);
      if (selectedPO) {
        newItems[index].po_number = selectedPO.po_number;
        // Do NOT auto-fill quantity - let user enter manually
        // Store SYSTEM UOM only (supplier UOM not used in Store Receiving)
        newItems[index].system_unit = selectedPO.system_unit || newItems[index].item_unit;
      }
    }
    
    // Auto-calculate quantity_accepted when quantity_received or quantity_rejected changes
    if (field === 'quantity_received' || field === 'quantity_rejected') {
      const received = parseFloat(field === 'quantity_received' ? value : newItems[index].quantity_received) || 0;
      const rejected = parseFloat(field === 'quantity_rejected' ? value : newItems[index].quantity_rejected) || 0;
      newItems[index].quantity_accepted = Math.max(0, received - rejected).toString();
      
      // Reset return_status if no rejected quantity
      if (rejected <= 0) {
        newItems[index].return_status = '';
      } else if (!newItems[index].return_status) {
        // Default to 'immediate' when rejection is entered
        newItems[index].return_status = 'immediate';
      }
    }
    
    setReceiptForm({ ...receiptForm, items: newItems });
  };

  const isPurchaseNature = (nature) => {
    return nature === 'Purchase-RMT/FNG' || nature === 'Purchase-CTM';
  };

  const handleCreateReceipt = async () => {
    // Validate
    if (!receiptForm.supplier_name) {
      toast.error('Please enter supplier name');
      return;
    }
    
    const validItems = receiptForm.items.filter(item => 
      item.item_id && item.quantity_received
    );
    
    // Validate Invoice/DC No. is provided
    if (!receiptForm.invoice_dc_no || receiptForm.invoice_dc_no.trim() === '') {
      toast.error('Invoice / DC No. is mandatory');
      return;
    }
    
    if (validItems.length === 0) {
      toast.error('Please add at least one item with quantity');
      return;
    }
    
    // Sub-location validation removed - now optional
    // const missingSubLocation = validItems.find(item => !item.sub_location || item.sub_location.trim() === '');
    // if (missingSubLocation) {
    //   toast.error(`Sub-Location is mandatory. Please select storage location for "${missingSubLocation.item_name}"`);
    //   return;
    // }
    
    // For Import Purchase, validate shipment is selected
    if (receiptForm.nature_of_inward === 'Import Purchase') {
      if (!receiptForm.import_shipment_id) {
        toast.error('Please select an Import Shipment for this import receipt');
        return;
      }
    } else if (isPurchaseNature(receiptForm.nature_of_inward)) {
      // For domestic purchase items, validate PO is selected and approved
      const missingPO = validItems.find(item => !item.po_id);
      if (missingPO) {
        toast.error(`Approved PO is required for purchase items. Please select PO for "${missingPO.item_name}"`);
        return;
      }
    } else if (receiptForm.nature_of_inward === 'Jobwork-Own') {
      // OJC inward redesign Phase 1 (Apr 2026) — every line must be linked to
      // an active OJC. Grandfathered OJCs still use the direct Receive flow and
      // should NOT create a Store Receipt.
      const missingOjc = validItems.find(it => !it.ojc_register_id);
      if (missingOjc) {
        toast.error(
          `Please select the OJC for "${missingOjc.item_name}". Jobwork-Own receipts must be linked to an active OJC.`
        );
        return;
      }
    }
    
    try {
      const payload = {
        gate_entry_id: selectedGateEntry.id,
        supplier_id: receiptForm.supplier_id || null,
        supplier_name: receiptForm.supplier_name,
        invoice_dc_no: receiptForm.invoice_dc_no || 'No Document',
        nature_of_inward: receiptForm.nature_of_inward,
        // Import shipment fields
        import_shipment_id: receiptForm.import_shipment_id || null,
        import_shipment_number: receiptForm.import_shipment_number || null,
        items: validItems.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          item_unit: item.item_unit,
          quantity_received: parseFloat(item.quantity_received),
          quantity_rejected: parseFloat(item.quantity_rejected) || 0,
          quantity_accepted: parseFloat(item.quantity_accepted) || parseFloat(item.quantity_received) - (parseFloat(item.quantity_rejected) || 0),
          return_status: parseFloat(item.quantity_rejected) > 0 ? (item.return_status || 'immediate') : null,
          quality_notes: item.quality_notes || null,
          po_id: item.po_id || null,
          po_number: item.po_number || null,
          po_line_item_index: item.po_line_item_index,
          sub_location: item.sub_location ? item.sub_location.trim().toUpperCase() : '',
          // System UOM only (supplier UOM not used in Store Receiving)
          system_unit: item.system_unit || item.item_unit || null,
          // Import-specific landed cost fields
          landed_cost_per_unit: item.landed_cost_per_unit || null,
          supplier_price_inr: item.supplier_price_inr || null,
          duty_per_unit: item.duty_per_unit || null,
          freight_per_unit: item.freight_per_unit || null,
          other_costs_per_unit: item.other_costs_per_unit || null,
          // Phase 1 (Apr 2026) — OJC linkage for Jobwork-Own receipts
          ojc_register_id: item.ojc_register_id || null
        })),
        sample_returnable: receiptForm.sample_returnable,
        debit_note_required: receiptForm.debit_note_required  // For return later with debit note
      };
      
      const response = await axios.post(`${API_URL}/store/receipt`, payload, { headers: getAuthHeader() });
      
      if (response.data.success) {
        let successMsg = `Store Receipt ${response.data.store_receipt_no} created with ${response.data.items_count} item(s)! Routed to: ${response.data.routed_to}`;
        
        // Show additional info if there are "Return Later" items
        if (response.data.return_later) {
          successMsg += `. Gate Pass ${response.data.return_later.gate_pass_number} created for ${response.data.return_later.items_count} rejected item(s).`;
          toast.success(successMsg, { duration: 6000 });
          toast.info(`SWS Task created for Purchase team to coordinate return.`, { duration: 4000 });
        } else {
          toast.success(successMsg);
        }
        
        setProcessDialogOpen(false);
        fetchData();
      }
    } catch (error) {
      console.error('Error creating receipt:', error);
      toast.error(error.response?.data?.detail || 'Failed to create receipt');
    }
  };

  // View receipt handler
  const handleViewReceipt = (receipt) => {
    setSelectedReceipt(receipt);
    setViewDialogOpen(true);
  };

  // Edit receipt handler - opens edit dialog with form pre-populated
  const handleEditReceipt = (receipt) => {
    if (receipt.purchase_entry_id) {
      toast.error('Cannot edit - GRN already created against this receipt');
      return;
    }
    setSelectedReceipt(receipt);
    setEditForm({
      supplier_id: receipt.supplier_id || '',
      supplier_name: receipt.supplier_name || '',
      invoice_dc_no: receipt.invoice_dc_no || '',
      nature_of_inward: receipt.nature_of_inward || '',
      sample_returnable: receipt.sample_returnable,
      debit_note_required: receipt.debit_note_required || false,
      items: (receipt.items || []).map(item => ({
        item_id: item.item_id,
        item_name: item.item_name,
        item_unit: item.item_unit || item.unit || 'NOS',
        unit: item.unit || item.item_unit || 'NOS',
        quantity_received: item.quantity_received || 0,
        quantity_rejected: item.quantity_rejected || item.quality_rejected_qty || 0,
        quantity_accepted: item.quantity_accepted || (item.quantity_received - (item.quantity_rejected || item.quality_rejected_qty || 0)),
        quality_status: item.quality_status || 'Accepted',
        quality_notes: item.quality_notes || '',
        return_status: item.return_status || '',
        po_id: item.po_id || '',
        po_number: item.po_number || '',
        po_line_item_index: item.po_line_item_index,
        sub_location: item.sub_location || '',
        system_unit: item.system_unit || item.item_unit || item.unit || ''
      }))
    });
    
    // Pre-fetch POs for existing items
    (receipt.items || []).forEach(item => {
      if (item.item_id) {
        fetchPOsForItem(item.item_id);
      }
    });
    
    setEditDialogOpen(true);
  };

  // Save edited receipt
  const handleSaveEdit = async () => {
    if (!selectedReceipt || !editForm) return;
    
    setEditSaving(true);
    try {
      const response = await axios.patch(
        `${API_URL}/store/receipt/${selectedReceipt.id}`,
        editForm,
        { headers: getAuthHeader() }
      );
      
      if (response.data.success) {
        toast.success('Receipt updated successfully');
        if (response.data.bin_card_adjustments?.length > 0) {
          toast.info(`Bin card adjusted for ${response.data.bin_card_adjustments.length} item(s)`);
        }
        setEditDialogOpen(false);
        setSelectedReceipt(null);
        setEditForm(null);
        fetchData();
      }
    } catch (error) {
      console.error('Error updating receipt:', error);
      toast.error(error.response?.data?.detail || 'Failed to update receipt');
    } finally {
      setEditSaving(false);
    }
  };

  // Update edit form item
  const updateEditFormItem = (index, field, value) => {
    const newItems = [...editForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Handle item selection - populate item details
    if (field === 'item_id') {
      const selectedItem = items.find(i => i.id === value);
      if (selectedItem) {
        newItems[index].item_name = selectedItem.name;
        newItems[index].item_unit = selectedItem.unit || 'NOS';
        newItems[index].system_unit = selectedItem.unit || 'NOS';
        // Clear PO selection when item changes
        newItems[index].po_id = '';
        newItems[index].po_number = '';
        newItems[index].po_line_item_index = null;
        // Fetch POs for this item
        fetchPOsForItem(value);
      }
    }
    
    // Handle PO selection - populate PO details
    if (field === 'po_id') {
      // Bug 9 fix: dropdown emits composite '<po_id>::<line_item_index>'; split
      // and store both keys so the backend edit handler targets the correct
      // PO line item.
      const [rawPoId, rawIdx] = String(value).split('::');
      const parsedIdx = rawIdx !== undefined && rawIdx !== '' ? parseInt(rawIdx, 10) : null;
      newItems[index].po_id = rawPoId;
      newItems[index].po_line_item_index = Number.isNaN(parsedIdx) ? null : parsedIdx;

      const itemPOs = posByItem[newItems[index].item_id] || [];
      const selectedPO = itemPOs.find(p => p.po_id === rawPoId && p.line_item_index === parsedIdx);
      if (selectedPO) {
        newItems[index].po_number = selectedPO.po_number;
      }
    }
    
    // Auto-calculate accepted qty
    if (field === 'quantity_received' || field === 'quantity_rejected') {
      const received = field === 'quantity_received' ? value : newItems[index].quantity_received;
      const rejected = field === 'quantity_rejected' ? value : newItems[index].quantity_rejected;
      newItems[index].quantity_accepted = Math.max(0, received - rejected);
    }
    
    setEditForm({ ...editForm, items: newItems });
  };

  // Add item to edit form
  const addEditFormItem = () => {
    setEditForm({
      ...editForm,
      items: [...editForm.items, {
        item_id: '',
        item_name: '',
        item_unit: '',
        unit: '',
        quantity_received: 0,
        quantity_rejected: 0,
        quantity_accepted: 0,
        quality_status: 'Accepted',
        quality_notes: '',
        return_status: '',
        po_id: '',
        po_number: '',
        po_line_item_index: null,
        sub_location: '',
        system_unit: ''
      }]
    });
  };

  // Remove item from edit form
  const removeEditFormItem = (index) => {
    if (editForm.items.length > 1) {
      const newItems = editForm.items.filter((_, i) => i !== index);
      setEditForm({ ...editForm, items: newItems });
    } else {
      toast.error('At least one item is required');
    }
  };

  // Check if edit form is for purchase nature
  const isEditPurchaseNature = editForm ? isPurchaseNature(editForm.nature_of_inward) : false;

  const pendingEntries = gateEntries.filter(e => e.status === 'pending');
  const recentReceipts = storeReceipts.slice(0, 200);  // Increased from 50 to 200
  const showIndentColumn = isPurchaseNature(receiptForm.nature_of_inward);
  const isJobworkOwn = receiptForm.nature_of_inward === 'Jobwork-Own';

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Warehouse className="w-7 h-7 text-blue-600" />
              Store Receiving
            </h1>
            <p className="text-muted-foreground">Process gate entries and create store receipts</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setLookupDialogOpen(true)}>
              <Search className="w-4 h-4 mr-2" />
              Lookup by Gate Entry No.
            </Button>
            <Button onClick={fetchData} variant="outline">
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold">{pendingEntries.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Processed Today</p>
                  <p className="text-2xl font-bold">
                    {storeReceipts.filter(r => r.receipt_date?.startsWith(new Date().toISOString().split('T')[0])).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Receipts</p>
                  <p className="text-2xl font-bold">{storeReceipts.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending">
              Pending Store Entries ({pendingEntries.length})
            </TabsTrigger>
            <TabsTrigger value="pending-materials">
              Pending Materials ({pendingMaterials.length})
            </TabsTrigger>
            <TabsTrigger value="receipts">
              Store Receipts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Pending Store Entries</CardTitle>
                <CardDescription>Gate entries waiting for store processing</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingEntries.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No pending gate entries</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Gate Entry No.</TableHead>
                        <TableHead>Date/Time</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Invoice/DC</TableHead>
                        <TableHead>Nature</TableHead>
                        <TableHead>Items (Gate Entry)</TableHead>
                        <TableHead className="text-center">Pkgs</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Remarks</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingEntries.map(entry => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-mono font-bold text-blue-600">
                            {entry.gate_entry_no}
                          </TableCell>
                          <TableCell>{formatDate(entry.entry_date, { includeTime: true })}</TableCell>
                          <TableCell>{entry.supplier_name_raw}</TableCell>
                          <TableCell>{entry.invoice_dc_no}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{entry.nature_of_inward}</Badge>
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            {entry.items?.length > 0 ? (
                              <div className="text-xs space-y-0.5">
                                {entry.items.slice(0, 3).map((item, idx) => (
                                  <div key={idx} className="truncate">
                                    {item.item_name}: {item.quantity} {item.uom || 'NOS'}
                                  </div>
                                ))}
                                {entry.items.length > 3 && (
                                  <div className="text-muted-foreground">+{entry.items.length - 3} more</div>
                                )}
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-center font-medium">{entry.num_packages}</TableCell>
                          <TableCell>{entry.vehicle_no || '-'}</TableCell>
                          <TableCell className="max-w-[150px] truncate">{entry.remarks || '-'}</TableCell>
                          <TableCell>
                            <Button size="sm" onClick={() => openProcessDialog(entry)}>
                              Process <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pending-materials" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Pending Materials</CardTitle>
                    <CardDescription>Expected materials from approved POs - guide security for incoming deliveries</CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select
                      value={pendingMaterialsLocationFilter}
                      onValueChange={setPendingMaterialsLocationFilter}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Filter by location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Locations</SelectItem>
                        {locations.map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.code} - {loc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={fetchPendingMaterials}>
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {pendingMaterialsLoading ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Clock className="w-8 h-8 mx-auto mb-3 animate-pulse" />
                    <p>Loading pending materials...</p>
                  </div>
                ) : pendingMaterials.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No pending materials</p>
                    <p className="text-sm mt-1">All approved POs have been fully received</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>PO Number</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Item Name</TableHead>
                        <TableHead className="text-right">Qty Ordered</TableHead>
                        <TableHead className="text-right">Qty Received</TableHead>
                        <TableHead className="text-right">Qty Pending</TableHead>
                        <TableHead className="text-center">Unit</TableHead>
                        <TableHead>Expected Date</TableHead>
                        <TableHead>Location</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingMaterials.map((item, idx) => (
                        <TableRow key={`${item.po_id}-${item.item_id}-${idx}`}>
                          <TableCell className="font-mono font-medium text-blue-600">
                            {item.po_number}
                          </TableCell>
                          <TableCell>{item.supplier_name}</TableCell>
                          <TableCell className="font-medium max-w-[200px] truncate" title={item.item_name}>
                            {item.item_name}
                          </TableCell>
                          <TableCell className="text-right">{item.qty_ordered}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{item.qty_received}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary" className="font-mono bg-amber-100 text-amber-800">
                              {item.qty_pending}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">{item.unit}</TableCell>
                          <TableCell>
                            {item.expected_delivery_date ? (
                              <span className={
                                new Date(item.expected_delivery_date) < new Date() 
                                  ? 'text-red-600 font-medium' 
                                  : ''
                              }>
                                {formatDate(item.expected_delivery_date)}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {item.location_code || item.location_name || '-'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="receipts" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Store Receipts</CardTitle>
                <CardDescription>Processed receipts and their routing status</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Receipt No.</TableHead>
                      <TableHead>Gate Entry</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Nature</TableHead>
                      <TableHead className="text-center">Items</TableHead>
                      <TableHead>Routed To</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentReceipts.map(receipt => (
                      <TableRow key={receipt.id}>
                        <TableCell className="font-mono font-bold">{receipt.store_receipt_no}</TableCell>
                        <TableCell className="font-mono text-sm">{receipt.gate_entry_no}</TableCell>
                        <TableCell>{formatDate(receipt.receipt_date)}</TableCell>
                        <TableCell>{receipt.supplier_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{receipt.nature_of_inward}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{receipt.items?.length || 1}</TableCell>
                        <TableCell>
                          <Badge className={routingColors[receipt.routed_to] || 'bg-gray-100'}>
                            {receipt.routed_to?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={receipt.routing_status === 'completed' ? 'default' : 'secondary'}>
                            {receipt.routing_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewReceipt(receipt)}
                              title="View Receipt"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditReceipt(receipt)}
                              disabled={!!receipt.purchase_entry_id}
                              title={receipt.purchase_entry_id ? "Cannot edit - GRN created" : "Edit Receipt"}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Lookup Dialog */}
        <Dialog open={lookupDialogOpen} onOpenChange={setLookupDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Lookup Gate Entry</DialogTitle>
              <DialogDescription>Enter the gate entry number to process</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Gate Entry Number</Label>
                <Input
                  placeholder="e.g., Unit 1-20260103-001"
                  value={lookupGateNo}
                  onChange={(e) => setLookupGateNo(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLookupDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleLookup}>
                <Search className="w-4 h-4 mr-2" />
                Lookup
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Process Gate Entry Dialog */}
        <Dialog open={processDialogOpen} onOpenChange={setProcessDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Process Gate Entry</DialogTitle>
              <DialogDescription>
                Create store receipt for gate entry: <span className="font-mono font-bold">{selectedGateEntry?.gate_entry_no}</span>
              </DialogDescription>
            </DialogHeader>
            
            {selectedGateEntry && (
              <div className="space-y-4 py-4">
                {/* Gate Entry Info */}
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <div className="grid grid-cols-4 gap-4">
                    <p><span className="text-muted-foreground">Supplier:</span> {selectedGateEntry.supplier_name_raw}</p>
                    <p><span className="text-muted-foreground">Invoice/DC:</span> {selectedGateEntry.invoice_dc_no}</p>
                    <p><span className="text-muted-foreground">Packages:</span> {selectedGateEntry.num_packages}</p>
                    <p><span className="text-muted-foreground">Vehicle:</span> {selectedGateEntry.vehicle_no || '-'}</p>
                  </div>
                  {selectedGateEntry.remarks && (
                    <p className="mt-2"><span className="text-muted-foreground">Remarks:</span> {selectedGateEntry.remarks}</p>
                  )}
                </div>

                {/* Supplier and Invoice */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Supplier (System)</Label>
                    <SearchableSelect
                      options={suppliers.map(s => ({ value: s.id, label: s.name }))}
                      value={receiptForm.supplier_id}
                      onValueChange={(value) => {
                        const supplier = suppliers.find(s => s.id === value);
                        setReceiptForm({ 
                          ...receiptForm, 
                          supplier_id: value,
                          supplier_name: supplier?.name || receiptForm.supplier_name
                        });
                        // Fetch import shipments if nature is already Import Purchase
                        if (receiptForm.nature_of_inward === 'Import Purchase') {
                          fetchImportShipments(value);
                        }
                      }}
                      placeholder="Select supplier..."
                      searchPlaceholder="Type to search..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Or Enter Manually *</Label>
                    <Input
                      placeholder="Supplier name"
                      value={receiptForm.supplier_name}
                      onChange={(e) => setReceiptForm({ ...receiptForm, supplier_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Invoice / DC No. *</Label>
                    <Input
                      value={receiptForm.invoice_dc_no}
                      onChange={(e) => setReceiptForm({ ...receiptForm, invoice_dc_no: e.target.value })}
                      placeholder="Enter Invoice or DC number"
                      required
                    />
                  </div>
                </div>

                {/* Nature */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nature of Inward *</Label>
                    <Select 
                      value={receiptForm.nature_of_inward}
                      onValueChange={(value) => {
                        setReceiptForm({ ...receiptForm, nature_of_inward: value });
                        // Fetch import shipments when Import Purchase is selected
                        if (value === 'Import Purchase' && receiptForm.supplier_id) {
                          fetchImportShipments(receiptForm.supplier_id);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {NATURE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {receiptForm.nature_of_inward === 'Samples Received' && (
                    <div className="space-y-2">
                      <Label>Sample Type *</Label>
                      <Select 
                        value={receiptForm.sample_returnable === true ? 'true' : receiptForm.sample_returnable === false ? 'false' : ''}
                        onValueChange={(value) => setReceiptForm({ ...receiptForm, sample_returnable: value === 'true' })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Is this sample returnable?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Returnable</SelectItem>
                          <SelectItem value="false">Non-Returnable</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Import Shipment Selector - shown when nature = Import Purchase */}
                {receiptForm.nature_of_inward === 'Import Purchase' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2 text-blue-800">
                      <Ship className="w-5 h-5" />
                      <span className="font-medium">Import Shipment Selection</span>
                    </div>
                    
                    {!receiptForm.supplier_id ? (
                      <p className="text-sm text-blue-700">Please select a supplier first to view pending import shipments.</p>
                    ) : importShipments.length === 0 ? (
                      <div className="space-y-2">
                        <p className="text-sm text-blue-700">No pending import shipments found for this supplier.</p>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => fetchImportShipments(receiptForm.supplier_id)}
                        >
                          Refresh Shipments
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label>Select Import Shipment *</Label>
                          <Select 
                            value={receiptForm.import_shipment_id}
                            onValueChange={handleShipmentSelect}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select import shipment..." />
                            </SelectTrigger>
                            <SelectContent>
                              {importShipments.map(shipment => (
                                <SelectItem key={shipment.id} value={shipment.id}>
                                  {shipment.shipment_number} - BOE: {shipment.boe_number || 'N/A'} - {shipment.line_items?.length || 0} items
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {selectedShipment && (
                          <div className="bg-white rounded p-3 text-sm space-y-2">
                            <div className="grid grid-cols-3 gap-2">
                              <p><span className="text-muted-foreground">Shipment:</span> {selectedShipment.shipment_number}</p>
                              <p><span className="text-muted-foreground">BOE:</span> {selectedShipment.boe_number || '-'}</p>
                              <p><span className="text-muted-foreground">PO:</span> {selectedShipment.po_number || '-'}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-2 border-t pt-2">
                              <p><span className="text-muted-foreground">Total Landed Cost:</span> <span className="font-medium">INR {selectedShipment.totals?.landed_cost?.toLocaleString() || '0'}</span></p>
                              <p><span className="text-muted-foreground">IGST (ITC):</span> INR {selectedShipment.totals?.igst?.toLocaleString() || '0'}</p>
                            </div>
                            <p className="text-xs text-green-700">Items will be auto-populated from shipment. Adjust accepted quantities as needed.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Purchase Alert */}
                {showIndentColumn && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      <strong>Approved PO Required:</strong> For purchase items, you must select an approved PO for each line item.
                    </p>
                  </div>
                )}

                {/* Jobwork-Own — no eligible (non-grandfathered) OJCs */}
                {isJobworkOwn && hasGrandfatheredOnly && (
                  <div
                    className="bg-amber-50 border border-amber-200 rounded-lg p-3"
                    data-testid="sr-no-eligible-ojc-banner"
                  >
                    <p className="text-sm text-amber-800">
                      <strong>No eligible OJCs for this supplier.</strong> All existing OJCs are pre-Phase 1 and must be received directly from OJC Register.
                    </p>
                  </div>
                )}

                {/* Items Section */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold">Line Items *</h4>
                    <Button size="sm" variant="outline" onClick={addItemRow}>
                      <Plus className="w-4 h-4 mr-1" /> Add Item
                    </Button>
                  </div>
                  
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[180px]">Item *</TableHead>
                        {showIndentColumn && (
                          <TableHead className="w-[200px]">PO No. *</TableHead>
                        )}
                        {isJobworkOwn && (
                          <TableHead className="w-[220px]">Linked OJC *</TableHead>
                        )}
                        <TableHead className="w-[90px]">Qty Received *</TableHead>
                        <TableHead className="w-[90px]">Qty Rejected</TableHead>
                        <TableHead className="w-[90px]">Qty Accepted</TableHead>
                        <TableHead className="w-[140px]">Return Status</TableHead>
                        <TableHead className="w-[80px]">System UOM</TableHead>
                        <TableHead className="w-[140px]">Sub-Location</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead className="w-[40px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {receiptForm.items.map((item, index) => {
                        const itemPOs = posByItem[item.item_id] || [];
                        const hasRejected = parseFloat(item.quantity_rejected) > 0;
                        return (
                          <TableRow key={index} className={hasRejected ? 'bg-red-50/50' : ''}>
                            <TableCell>
                              <SearchableSelect
                                options={items.map(i => ({ 
                                  value: i.id, 
                                  label: `${i.name} (${i.unit || 'PCS'})`,
                                  code: i.item_code || '',
                                  category: i.category || ''
                                }))}
                                value={item.item_id}
                                onValueChange={(value) => updateItem(index, 'item_id', value)}
                                placeholder="Select item..."
                                searchPlaceholder="Search items..."
                              />
                            </TableCell>
                            {showIndentColumn && (
                              <TableCell>
                                <SearchableSelect
                                  options={itemPOs.map(p => ({ 
                                    // Bug 9 fix: composite key '<po_id>::<line_item_index>'
                                    // so duplicate-item rows on the same PO remain
                                    // distinguishable in the dropdown. See updateItem.
                                    value: `${p.po_id}::${p.line_item_index}`,
                                    label: `${p.po_number} (Pending: ${p.pending_qty} ${p.so_number ? `| SO: ${p.so_number}` : ''})`
                                  }))}
                                  value={item.po_id != null && item.po_id !== '' ? `${item.po_id}::${item.po_line_item_index}` : ''}
                                  onValueChange={(value) => updateItem(index, 'po_id', value)}
                                  placeholder={item.item_id ? "Select PO..." : "Select item first"}
                                  searchPlaceholder="Search POs..."
                                  emptyText={item.item_id ? "No approved POs found for this item" : "Select item first"}
                                  disabled={!item.item_id}
                                />
                              </TableCell>
                            )}
                            {isJobworkOwn && (
                              <TableCell>
                                <SearchableSelect
                                  options={openOjcsForSupplier.map(o => ({
                                    value: o.id,
                                    label: `${o.ojc_number || o.id} · pending ${o.quantity_pending}`
                                  }))}
                                  value={item.ojc_register_id || ''}
                                  onValueChange={(value) => updateItem(index, 'ojc_register_id', value)}
                                  placeholder={openOjcsForSupplier.length ? "Select OJC..." : (hasGrandfatheredOnly ? "No eligible OJCs (all pre-Phase 1)" : "No open OJCs")}
                                  searchPlaceholder="Search OJCs..."
                                  emptyText={hasGrandfatheredOnly ? "No eligible OJCs for this supplier. All existing OJCs are pre-Phase 1 and must be received directly from OJC Register." : "No open OJCs for this supplier"}
                                  disabled={!openOjcsForSupplier.length}
                                  data-testid={`sr-linked-ojc-select-${index}`}
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0"
                                className="w-20 min-w-[70px]"
                                value={item.quantity_received}
                                onChange={(e) => updateItem(index, 'quantity_received', e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0"
                                className={`w-20 min-w-[70px] ${hasRejected ? 'border-red-300 bg-red-50' : ''}`}
                                value={item.quantity_rejected}
                                onChange={(e) => updateItem(index, 'quantity_rejected', e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <Badge variant={hasRejected ? "secondary" : "outline"} className="font-mono">
                                {item.quantity_accepted || '0'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {hasRejected ? (
                                <Select 
                                  value={item.return_status || 'immediate'}
                                  onValueChange={(value) => updateItem(index, 'return_status', value)}
                                >
                                  <SelectTrigger className={item.return_status === 'later' ? 'border-orange-300 bg-orange-50' : ''}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {RETURN_STATUS_OPTIONS.map(opt => (
                                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-sm font-medium">{item.system_unit || item.item_unit || '-'}</span>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={item.sub_location || ''}
                                onValueChange={(val) => updateItem(index, 'sub_location', val)}
                              >
                                <SelectTrigger className="w-44 min-w-[140px]">
                                  <SelectValue placeholder="Select location..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {subLocations.map(sl => (
                                    <SelectItem key={sl.id} value={sl.code}>
                                      {sl.code} - {sl.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                placeholder="Notes..."
                                value={item.quality_notes}
                                onChange={(e) => updateItem(index, 'quality_notes', e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              {receiptForm.items.length > 1 && (
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  onClick={() => removeItemRow(index)}
                                  className="h-8 w-8 text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Return Later Info */}
                {receiptForm.items.some(item => parseFloat(item.quantity_rejected) > 0 && item.return_status === 'later') && (
                  <div className="space-y-3">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <p className="text-sm text-orange-800 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>
                          <strong>&quot;Return Later&quot; selected:</strong> A <strong>Gate Pass</strong> (draft) and <strong>SWS Task</strong> for Purchase team will be auto-created to coordinate the return of rejected goods with the supplier.
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <Checkbox
                        id="debit_note_required"
                        checked={receiptForm.debit_note_required}
                        onCheckedChange={(checked) => setReceiptForm({ ...receiptForm, debit_note_required: checked })}
                      />
                      <Label htmlFor="debit_note_required" className="text-sm font-medium cursor-pointer flex items-center gap-2 text-red-800">
                        <AlertCircle className="w-4 h-4" />
                        Debit Note Required (for returned goods)
                      </Label>
                    </div>
                  </div>
                )}

                {/* Routing Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Auto-Routing:</strong> Based on nature &quot;{receiptForm.nature_of_inward}&quot;, this receipt will be routed to the appropriate department.
                  </p>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setProcessDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateReceipt}>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Create Store Receipt
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Receipt Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>View Store Receipt</DialogTitle>
              <DialogDescription>
                {selectedReceipt?.store_receipt_no} - {selectedReceipt?.supplier_name}
              </DialogDescription>
            </DialogHeader>
            {selectedReceipt && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-xs">Receipt No.</Label>
                    <p className="font-mono font-bold">{selectedReceipt.store_receipt_no}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Gate Entry</Label>
                    <p className="font-mono">{selectedReceipt.gate_entry_no}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Date</Label>
                    <p>{formatDate(selectedReceipt.receipt_date)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Supplier</Label>
                    <p>{selectedReceipt.supplier_name}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Invoice/DC No.</Label>
                    <p>{selectedReceipt.invoice_dc_no || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Nature</Label>
                    <Badge variant="outline">{selectedReceipt.nature_of_inward}</Badge>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Routed To</Label>
                    <Badge className={routingColors[selectedReceipt.routed_to] || 'bg-gray-100'}>
                      {selectedReceipt.routed_to?.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">GRN Status</Label>
                    {selectedReceipt.purchase_entry_id ? (
                      <Badge variant="default">GRN Created</Badge>
                    ) : (
                      <Badge variant="secondary">Pending GRN</Badge>
                    )}
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <Label className="text-sm font-semibold mb-2 block">Items</Label>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>PO No.</TableHead>
                        <TableHead className="text-right">Qty Received</TableHead>
                        <TableHead className="text-right">Qty Rejected</TableHead>
                        <TableHead className="text-right">Qty Accepted</TableHead>
                        <TableHead>Quality</TableHead>
                        <TableHead>Sub Location</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedReceipt.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell className="font-mono text-xs">{item.po_number || '-'}</TableCell>
                          <TableCell className="text-right">{item.quantity_received} {item.unit || item.item_unit}</TableCell>
                          <TableCell className="text-right text-red-600">{item.quantity_rejected || item.quality_rejected_qty || 0}</TableCell>
                          <TableCell className="text-right text-green-600">{item.quantity_accepted || (item.quantity_received - (item.quantity_rejected || item.quality_rejected_qty || 0))}</TableCell>
                          <TableCell>
                            <Badge variant={item.quality_status === 'Accepted' ? 'default' : item.quality_status === 'Rejected' ? 'destructive' : 'secondary'}>
                              {item.quality_status}
                            </Badge>
                          </TableCell>
                          <TableCell>{item.sub_location || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
              {selectedReceipt && !selectedReceipt.purchase_entry_id && (
                <Button onClick={() => { setViewDialogOpen(false); handleEditReceipt(selectedReceipt); }}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Receipt Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Store Receipt</DialogTitle>
              <DialogDescription>
                {selectedReceipt?.store_receipt_no} - Modify receipt details before GRN creation
              </DialogDescription>
            </DialogHeader>
            {editForm && (
              <div className="space-y-4">
                {/* Header Fields */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Supplier *</Label>
                    <SearchableSelect
                      options={suppliers.map(s => ({ value: s.id, label: s.name }))}
                      value={editForm.supplier_id}
                      onValueChange={(val) => {
                        const supplier = suppliers.find(s => s.id === val);
                        setEditForm({ ...editForm, supplier_id: val, supplier_name: supplier?.name || '' });
                      }}
                      placeholder="Select supplier"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Invoice/DC No. *</Label>
                    <Input
                      value={editForm.invoice_dc_no}
                      onChange={(e) => setEditForm({ ...editForm, invoice_dc_no: e.target.value })}
                      placeholder="Invoice or DC number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nature of Inward *</Label>
                    <Select
                      value={editForm.nature_of_inward}
                      onValueChange={(val) => setEditForm({ ...editForm, nature_of_inward: val })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select nature" />
                      </SelectTrigger>
                      <SelectContent>
                        {NATURE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Additional Options Row */}
                <div className="flex items-center gap-6 pt-2">
                  {editForm.nature_of_inward === 'Samples Received' && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="edit-sample-returnable"
                        checked={editForm.sample_returnable === true}
                        onCheckedChange={(checked) => setEditForm({ ...editForm, sample_returnable: checked })}
                      />
                      <Label htmlFor="edit-sample-returnable" className="text-sm cursor-pointer">Sample Returnable</Label>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="edit-debit-note"
                      checked={editForm.debit_note_required === true}
                      onCheckedChange={(checked) => setEditForm({ ...editForm, debit_note_required: checked })}
                    />
                    <Label htmlFor="edit-debit-note" className="text-sm cursor-pointer">Debit Note Required (for rejected goods)</Label>
                  </div>
                </div>

                {/* Purchase Alert */}
                {isEditPurchaseNature && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      <strong>Approved PO Required:</strong> For purchase items, you must select an approved PO for each line item.
                    </p>
                  </div>
                )}

                {/* Items Section */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold">Line Items *</h4>
                    <Button size="sm" variant="outline" onClick={addEditFormItem}>
                      <Plus className="w-4 h-4 mr-1" /> Add Item
                    </Button>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[180px]">Item *</TableHead>
                          {isEditPurchaseNature && (
                            <TableHead className="w-[200px]">PO No. *</TableHead>
                          )}
                          <TableHead className="w-[90px]">Qty Received *</TableHead>
                          <TableHead className="w-[90px]">Qty Rejected</TableHead>
                          <TableHead className="w-[90px]">Qty Accepted</TableHead>
                          <TableHead className="w-[120px]">Return Status</TableHead>
                          <TableHead className="w-[80px]">System UOM</TableHead>
                          <TableHead className="w-[140px]">Sub-Location</TableHead>
                          <TableHead className="w-[120px]">Quality Status</TableHead>
                          <TableHead>Notes</TableHead>
                          <TableHead className="w-[40px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                    <TableBody>
                      {editForm.items?.map((item, idx) => {
                        const itemPOs = posByItem[item.item_id] || [];
                        const hasRejected = parseFloat(item.quantity_rejected) > 0;
                        return (
                          <TableRow key={idx} className={hasRejected ? 'bg-red-50/50' : ''}>
                            <TableCell>
                              <SearchableSelect
                                options={items.map(i => ({ 
                                  value: i.id, 
                                  label: `${i.name} (${i.unit || 'PCS'})`,
                                  code: i.item_code || '',
                                  category: i.category || ''
                                }))}
                                value={item.item_id}
                                onValueChange={(value) => updateEditFormItem(idx, 'item_id', value)}
                                placeholder="Select item..."
                                searchPlaceholder="Search items..."
                              />
                            </TableCell>
                            {isEditPurchaseNature && (
                              <TableCell>
                                <SearchableSelect
                                  options={itemPOs.map(p => ({ 
                                    // Bug 9 fix: composite key mirroring the create-form
                                    // dropdown so duplicate-item PO rows remain
                                    // distinguishable. See updateEditFormItem.
                                    value: `${p.po_id}::${p.line_item_index}`,
                                    label: `${p.po_number} (Pending: ${p.pending_qty})`
                                  }))}
                                  value={item.po_id != null && item.po_id !== '' ? `${item.po_id}::${item.po_line_item_index}` : ''}
                                  onValueChange={(value) => updateEditFormItem(idx, 'po_id', value)}
                                  placeholder={item.item_id ? "Select PO..." : "Select item first"}
                                  searchPlaceholder="Search POs..."
                                  emptyText={item.item_id ? "No approved POs found" : "Select item first"}
                                  disabled={!item.item_id}
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0"
                                className="w-20 min-w-[70px]"
                                value={item.quantity_received}
                                onChange={(e) => updateEditFormItem(idx, 'quantity_received', parseFloat(e.target.value) || 0)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0"
                                className={`w-20 min-w-[70px] ${hasRejected ? 'border-red-300 bg-red-50' : ''}`}
                                value={item.quantity_rejected}
                                onChange={(e) => updateEditFormItem(idx, 'quantity_rejected', parseFloat(e.target.value) || 0)}
                              />
                            </TableCell>
                            <TableCell>
                              <Badge variant={hasRejected ? "secondary" : "outline"} className="font-mono">
                                {item.quantity_accepted || 0}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {hasRejected ? (
                                <Select 
                                  value={item.return_status || 'immediate'}
                                  onValueChange={(value) => updateEditFormItem(idx, 'return_status', value)}
                                >
                                  <SelectTrigger className={`w-28 ${item.return_status === 'later' ? 'border-orange-300 bg-orange-50' : ''}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {RETURN_STATUS_OPTIONS.map(opt => (
                                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-sm font-medium">{item.system_unit || item.item_unit || '-'}</span>
                            </TableCell>
                            <TableCell>
                              <SearchableSelect
                                options={subLocations.map(s => ({ value: s.code, label: `${s.code} - ${s.name}` }))}
                                value={item.sub_location || ''}
                                onValueChange={(val) => updateEditFormItem(idx, 'sub_location', val)}
                                placeholder="Select"
                                className="w-32"
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                value={item.quality_status}
                                onValueChange={(val) => updateEditFormItem(idx, 'quality_status', val)}
                              >
                                <SelectTrigger className="w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {QUALITY_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                placeholder="Notes"
                                value={item.quality_notes || ''}
                                onChange={(e) => updateEditFormItem(idx, 'quality_notes', e.target.value)}
                                className="w-24 min-w-[80px]"
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeEditFormItem(idx)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-8"
                                title="Remove item"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditForm(null); }}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={editSaving}>
                {editSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
